import React from 'react';
import { Search } from 'lucide-react';
import { ConversationStatus } from '@/services/integrations/whatsappInboxService';
import { useUser } from '@/contexts/UserContext';
import { useUsers } from '@/contexts/UsersContext';

interface SearchFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  unreadOnly: boolean;
  onToggleUnread: () => void;
  status: ConversationStatus | undefined;
  onStatusChange: (status: ConversationStatus | undefined) => void;
  unreadCount: number;
  assignedUserUuid: string | undefined;
  onAssignedUserChange: (uuid: string | undefined) => void;
}

// "Open" = the customer wrote last and is waiting for our reply (server-side rule,
// whatsapp_conversation_service.list_conversations).
const STATUS_PILLS: { value: ConversationStatus | undefined; label: string; hint: string }[] = [
  { value: undefined, label: 'Open + Resolved', hint: 'Every chat except archived ones' },
  { value: 'open', label: 'Open', hint: 'The customer wrote last and is waiting for our reply' },
  { value: 'resolved', label: 'Resolved', hint: 'Marked resolved by an agent' },
  { value: 'archived', label: 'Archived', hint: 'Archived chats' },
];

/**
 * The "assigned to" agent filter is shown only to super admins (FE-21) —
 * scope.txt §19 marks "view all conversations" as Admin-only by default,
 * and this control is meaningless for anyone else: their lead_scope already
 * restricts them server-side (BE-09), so the filter would just be a
 * confusing no-op for a plain agent.
 */
const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  search, onSearchChange, unreadOnly, onToggleUnread, status, onStatusChange, unreadCount,
  assignedUserUuid, onAssignedUserChange,
}) => {
  const { userData } = useUser();
  const isSuperAdmin = userData?.data?.role?.role_id === 9401;
  const { activeUsers } = useUsers();

  return (
    <div className="px-4 pt-2.5 pb-2.5 border-b border-gray-100">
      <div className="flex items-center gap-2 h-9 border border-gray-200 rounded-lg px-2.5 bg-gray-50 mb-2">
        <Search className="w-[15px] h-[15px] text-gray-400 flex-shrink-0" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Name, phone or message text…"
          className="flex-1 text-[12.5px] text-gray-900 bg-transparent border-0 outline-none placeholder:text-gray-400"
        />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          onClick={onToggleUnread}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${
            unreadOnly ? 'bg-[#153d23] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Unread {unreadCount > 0 ? unreadCount : ''}
        </button>
        {STATUS_PILLS.map((pill) => (
          <button
            key={pill.label}
            title={pill.hint}
            onClick={() => onStatusChange(pill.value)}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${
              status === pill.value ? 'bg-[#153d23] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {pill.label}
          </button>
        ))}
      </div>
      {isSuperAdmin && (
        <select
          value={assignedUserUuid || ''}
          onChange={(e) => onAssignedUserChange(e.target.value || undefined)}
          className="w-full h-8 text-[11.5px] border border-gray-200 rounded-lg px-2 bg-white text-gray-600 outline-none focus:border-brandColor"
        >
          <option value="">All agents</option>
          {activeUsers.map((u) => (
            <option key={u.uuid} value={u.uuid}>{u.userName}</option>
          ))}
        </select>
      )}
    </div>
  );
};

export default SearchFilterBar;
